/**
 * CustomerEditModal - Modal for editing customer details
 */

import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Space, message } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import CustomerForm from "./CustomerForm";
import { updateCustomer, createCustomer } from "../services/customerService";

const CustomerEditModal = ({
  customer,
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState({});

  const isNewCustomer = !customer || !customer.customerId;

  useEffect(() => {
    if (open) {
      if (isNewCustomer) {
        // New customer - empty form
        form.resetFields();
        setOriginalValues({});
        setHasChanges(false);
      } else {
        // Existing customer - populate form
        const initialValues = {
          name: customer.name || "",
          phone: customer.phone && !customer.phone.startsWith("TEMP_") ? customer.phone : "",
          email: customer.email || "",
          address: customer.address || "",
        };
        form.setFieldsValue(initialValues);
        setOriginalValues(initialValues);
        setHasChanges(false);
      }
    }
  }, [open, customer, form, isNewCustomer]);

  const handleValuesChange = (changedValues, allValues) => {
    // Check if there are any changes
    const hasChanges = Object.keys(changedValues).some(
      (key) => allValues[key] !== originalValues[key]
    );
    setHasChanges(hasChanges);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      setLoading(true);

      let result;
      if (isNewCustomer) {
        // Create new customer
        result = await createCustomer(values);
      } else {
        // Update existing customer
        // Only send changed fields
        const changes = {};
        Object.keys(values).forEach((key) => {
          if (values[key] !== originalValues[key]) {
            changes[key] = values[key] || null;
          }
        });

        if (Object.keys(changes).length === 0) {
          message.info("No changes to save");
          setLoading(false);
          return;
        }

        result = await updateCustomer(customer.customerId, changes);
      }

      if (result.success) {
        message.success(
          isNewCustomer
            ? "Customer created successfully"
            : "Customer updated successfully"
        );
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        message.error(result.error || "Failed to save customer");
      }
    } catch (error) {
      console.error("Validation or save error:", error);
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      message.error(error.message || "Failed to save customer");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setHasChanges(false);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          {isNewCustomer ? "Create New Customer" : `Edit Customer: ${customer?.name || ""}`}
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} icon={<CloseOutlined />}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          icon={<SaveOutlined />}
          loading={loading}
          disabled={!isNewCustomer && !hasChanges}
        >
          {isNewCustomer ? "Create Customer" : "Save Changes"}
        </Button>,
      ]}
      width={600}
      destroyOnHidden
    >
      <CustomerForm
        form={form}
        onFinish={handleSave}
        loading={loading}
        onValuesChange={handleValuesChange}
      />
    </Modal>
  );
};

export default CustomerEditModal;

